/**
 * Workspace Service
 *
 * Fetches workspace context from elauth to identify which workspace
 * the user is setting up accounting for.
 */

import { AUTH_ORIGIN } from '../utils/domain'

// elauth's backend has CORS enabled on /api/v1/auth/* — call its origin
// directly rather than relying on a same-origin proxy path.
const AUTH_API_URL = `${AUTH_ORIGIN}/api/v1`

export interface WorkspaceInfo {
  workspaceId: string
  name: string
  shortId: string
  tag: string
}

interface ElAuthWorkspacesResponse {
  status: string
  message: string
  data: {
    workspaces: RawWorkspace[]
    default_workspace_id: string
  }
}

interface RawWorkspace {
  workspace_id: string
  name: string
  short_id: string
  tag: string
}

let cachedWorkspace: WorkspaceInfo | null = null
let cachedAllWorkspaces: WorkspaceInfo[] | null = null

const WORKSPACE_COOKIE_NAME = 'elauth_workspace_id'

/**
 * Read the workspace ID cookie set by elauth's workspace picker.
 * This cookie is set on the .elsapiens.test domain so it's accessible here.
 */
function getWorkspaceCookie(): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${WORKSPACE_COOKIE_NAME}=([^;]*)`))
  return match?.[1] ? decodeURIComponent(match[1]) : null
}

/**
 * Resolve which workspace to use: cookie (workspace picker) > default > first.
 * Extracted to reduce complexity in getCurrentWorkspace.
 */
function resolveWorkspace(
  workspaces: RawWorkspace[],
  defaultId: string | undefined,
): RawWorkspace | undefined {
  const cookieId = getWorkspaceCookie()
  return (
    (cookieId ? workspaces.find(ws => ws.workspace_id === cookieId) : undefined) ??
    (defaultId ? workspaces.find(ws => ws.workspace_id === defaultId) : undefined) ??
    workspaces[0]
  )
}

/**
 * Fetch all workspaces from elauth.
 * Uses the shared .elsapiens.test cookie for authentication.
 */
export async function getAllWorkspaces(): Promise<WorkspaceInfo[]> {
  if (cachedAllWorkspaces) {return cachedAllWorkspaces}

  try {
    const resp = await fetch(`${AUTH_API_URL}/auth/workspaces`, {
      credentials: 'include',
      signal: AbortSignal.timeout(10000),
    })

    if (!resp.ok) {
      console.warn('[WorkspaceService] Failed to fetch workspaces:', resp.status)
      return []
    }

    const data = (await resp.json()) as ElAuthWorkspacesResponse
    const workspaces = data.data?.workspaces

    if (!workspaces || workspaces.length === 0) {return []}

    cachedAllWorkspaces = workspaces.map(ws => ({
      workspaceId: ws.workspace_id,
      name: ws.name,
      shortId: ws.short_id,
      tag: ws.tag,
    }))

    return cachedAllWorkspaces
  } catch (error) {
    console.warn('[WorkspaceService] Error fetching workspaces:', error)
    return []
  }
}

/**
 * Fetch the current workspace info from elauth.
 * Uses the shared .elsapiens.test cookie for authentication.
 */
export async function getCurrentWorkspace(): Promise<WorkspaceInfo | null> {
  if (cachedWorkspace) {
    return cachedWorkspace
  }

  try {
    const resp = await fetch(`${AUTH_API_URL}/auth/workspaces`, {
      credentials: 'include',
      signal: AbortSignal.timeout(10000),
    })

    if (!resp.ok) {
      console.warn('[WorkspaceService] Failed to fetch workspaces:', resp.status)
      return null
    }

    const data = (await resp.json()) as ElAuthWorkspacesResponse

    const workspaces = data.data?.workspaces
    const defaultId = data.data?.default_workspace_id

    if (!workspaces || workspaces.length === 0) {
      return null
    }

    // Cache all workspaces too
    cachedAllWorkspaces = workspaces.map(ws => ({
      workspaceId: ws.workspace_id,
      name: ws.name,
      shortId: ws.short_id,
      tag: ws.tag,
    }))

    // Priority: cookie (set by workspace picker) > default_workspace_id > first
    const workspace = resolveWorkspace(workspaces, defaultId)

    /* v8 ignore next 3 */
    if (!workspace) {
      return null
    }

    cachedWorkspace = {
      workspaceId: workspace.workspace_id,
      name: workspace.name,
      shortId: workspace.short_id,
      tag: workspace.tag,
    }

    return cachedWorkspace
  } catch (error) {
    console.warn('[WorkspaceService] Error fetching workspace:', error)
    return null
  }
}

/**
 * Clear cached workspace (e.g. on logout or workspace switch).
 */
export function clearWorkspaceCache(): void {
  cachedWorkspace = null
  cachedAllWorkspaces = null
}
